/**
 * Settings → Lead sources (L1b). ADMIN ONLY.
 *
 * ---------------------------------------------------------------------------
 * THIS PAGE IS THE INTEGRATION
 * ---------------------------------------------------------------------------
 * Four lead companies, a website and Meta each need a URL, a key and a set of
 * instructions, and each will switch over on their own schedule across weeks.
 * Every one of those handovers happens here rather than in a deploy: the URL is
 * copied, the key is revealed or rotated, the first payload is read, the mapping
 * is corrected and the held lead is reprocessed.
 *
 * "Send test lead" exists because the providers are not ready and Friday is. It
 * posts a real sample over HTTP to the real URL with the real key, which is the
 * only way to prove the parts that actually fail — DNS, routing, the key, the
 * raw store — rather than the parts that never do.
 *
 * ---------------------------------------------------------------------------
 * THE KEY IS FETCHED WHEN ASKED FOR, NEVER LISTED
 * ---------------------------------------------------------------------------
 * The list carries a six-character preview. Reveal, rotate and the instructions
 * text each ask the server for the real one, and each of those asks is logged
 * server-side with the admin's address. A secret the whole panel holds is a
 * secret in a browser cache and in every screenshot of this page.
 */

import React, { useState } from 'react';
import { CRM } from '../lib/theme';
import { useApi, useApiClient } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { userTz, formatDateTimeInTz } from '../lib/time';
import { Icons } from '../lib/icons';
import { useAction } from '../hooks/useAction';

const T = { ...CRM, mono: "'DM Mono', monospace" };

const input: React.CSSProperties = {
  width: '100%', padding: '7px 9px', background: T.panel2, border: `1px solid ${T.border}`,
  color: T.text, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box',
};
const ghost: React.CSSProperties = {
  padding: '5px 10px', background: T.panel2, color: T.muted,
  border: `1px solid ${T.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '11px',
};
const primary: React.CSSProperties = {
  padding: '5px 12px', background: T.accentFill, color: 'var(--crm-on-fill)', border: 'none',
  borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
};

/** Every status the log can hold, and what each one means at a glance. */
const STATUS: Record<string, { label: string; color: string; hint: string }> = {
  created: { label: 'Created', color: T.green, hint: 'Became a lead.' },
  duplicate_flagged: { label: 'Duplicate', color: T.amber, hint: 'Became a lead, flagged against an earlier one. No acknowledgement sent.' },
  retry_ignored: { label: 'Retry', color: T.dim, hint: 'Identical to something received in the last 24h. No lead created.' },
  held_unmapped: { label: 'Held', color: T.amber, hint: 'Stored, but not enough could be mapped to make a lead. Fix the mapping and reprocess.' },
  rejected: { label: 'Rejected', color: T.red, hint: 'Refused before storing.' },
  error: { label: 'Error', color: T.red, hint: 'Stored, then something failed on our side.' },
};
const statusOf = (s: string) => STATUS[s] ?? { label: s, color: T.muted, hint: '' };

/** The fields a mapping may target. Mirrors the server's allow-list. */
const TARGET_FIELDS = [
  'full_name', 'first_name', 'last_name', 'email', 'phone',
  'move_date', 'move_size', 'move_type', 'internal_notes',
  'origin_address', 'origin_street', 'origin_city', 'origin_province', 'origin_postal',
  'destination_address', 'destination_street', 'destination_city', 'destination_province', 'destination_postal',
];

/**
 * The field names balticvanlines.ca/get-a-quote/ actually posts, read from the
 * live page. Kadence names its fields with hashes, so no alias dictionary could
 * ever guess these — offered as one click rather than eight lines of typing that
 * have to be transcribed correctly.
 */
const KNOWN_MAPPINGS: Record<string, Record<string, string>> = {
  'website-quote': {
    'fieldd44f95-f0': 'full_name',
    'field43daed-d5': 'email',
    'fieldbd8202-0a': 'phone',
    'fieldb8716b-a0': 'origin_address',
    'field02bf5b-72': 'destination_address',
    'fielda3c483-71': 'move_date',
    'field2ca9fe-4b': 'move_type',
    'field2b698f-34': 'internal_notes',
  },
};

const copy = async (text: string, done: (s: string) => void) => {
  try { await navigator.clipboard.writeText(text); done('Copied'); }
  catch { done('Could not copy — select it and press ⌘C'); }
  window.setTimeout(() => done(''), 2500);
};

function instructionsText(name: string, url: string, key: string): string {
  return `Lead posting — ${name}

URL:     ${url}
Method:  POST

Authentication — send the key as a header:
    X-Intake-Key: ${key}
or, if you can only configure a URL, put it in the query string:
    ${url}?key=${key}

Body formats we accept:
    application/json
    application/x-www-form-urlencoded
    multipart/form-data
    text/xml  or  application/xml

Fields we would like (we recognise the usual variations of these names
automatically — FirstName, fname, first_name and so on all work):
    first name and last name, or a single full name
    email
    phone
    move date
    origin city / province or state / postal or zip code
    destination city / province or state / postal or zip code
    move size, or number of bedrooms
    comments or notes

Anything else you send is kept and shown to our team. Nothing is discarded.

What we answer:
    200   received
    401   the key is missing or wrong
    413   the body is larger than 256 KB
    429   more than 60 posts in one minute — please retry after a minute
    5xx   we failed to store it — please retry

Please send one test lead once you are set up, and let us know, so we can
confirm it arrived and map your field names correctly.
`;
}

export function LeadSourcesCard() {
  const api = useApiClient();
  const { user } = useAuth();
  const tz = userTz(user);
  // Belt and braces: this card only renders inside Settings' admin block, and
  // the reveal endpoint is admin-only server-side. The button that puts a secret
  // on the clipboard says so for itself too.
  const isAdmin = user?.role === 'admin';
  const { data, error, refetch } = useApi<any>(async () => (await api.getLeadSources()).data);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [modal, setModal] = useState<{ kind: 'instructions' | 'log'; source?: any; logId?: string; text?: string } | null>(null);

  const sources: any[] = data?.sources ?? [];

  const runGuard = useAction(); // one at a time: a second click is ignored
  const run = (id: string, fn: () => Promise<void>) => runGuard.run(() => runNow(id, fn));
  const runNow = async (id: string, fn: () => Promise<void>) => {
    setBusy(id); setNote('');
    try { await fn(); } catch (e: any) { setNote(e?.message || 'That did not work.'); }
    finally { setBusy(null); }
  };

  const toggleActive = (s: any) => run(s.id, async () => {
    await api.updateLeadSource(s.id, { active: !s.active });
    await refetch();
  });

  const sendTest = (s: any) => run(s.id, async () => {
    const res = await api.sendTestLead(s.id);
    const d: any = res.data;
    // When API_BASE_URL is unset the server tests its OWN origin instead of the
    // public one — correct (it must not post into another environment), but a
    // pass then says nothing about whether api.balticvanlines.ca resolves.
    const caveat = d?.testedPublicUrl === false
      ? ` (posted to ${d.url} — API_BASE_URL is not set on this server, so the public address was NOT tested)`
      : '';
    setNote(
      d?.status === 'created' ? `Test lead created — Job #${d.jobNo}. Check New Leads, and your inbox for the acknowledgement.${caveat}`
      : d?.status === 'duplicate_flagged' ? `Test lead created and flagged as a possible duplicate (Job #${d.jobNo}) — expected, since you have sent one before. No acknowledgement this time.`
      : d?.status === 'held_unmapped' ? 'Posted and stored, but held: nothing could be mapped. Open the log row to see the payload.'
      : `Posted. Status: ${d?.status ?? 'unknown'}.${caveat}`
    );
    await refetch();
  });

  const rotate = (s: any) => run(s.id, async () => {
    if (!window.confirm(
      `Rotate the key for ${s.displayName}?\n\n` +
      `The current key stops working immediately. ${s.displayName} will not be able to post until you give them the new one.`
    )) return;
    const res = await api.rotateLeadSourceKey(s.id);
    window.prompt(`New key for ${s.displayName} — copy it now and send it to them:`, (res.data as any)?.key ?? '');
    await refetch();
  });

  const reveal = (s: any) => run(s.id, async () => {
    const res = await api.revealLeadSourceKey(s.id);
    window.prompt(`Intake key for ${s.displayName}:`, (res.data as any)?.key ?? '');
  });

  /**
   * THE URL A DEVELOPER CAN ACTUALLY PASTE.
   *
   * "Copy URL" copies the bare endpoint, which is unauthenticated and answers
   * 401 to everything — and that is exactly what reached the website developer,
   * who wired it up and got nothing. The key has to travel with it.
   *
   * The key is fetched at click time rather than held in the list: the panel
   * carries a six-character preview precisely so a secret is not sitting in
   * every admin's browser the whole time it is open.
   */
  const copyFullUrl = (s: any) => run(s.id, async () => {
    const res = await api.revealLeadSourceKey(s.id);
    const key = String((res.data as any)?.key ?? '');
    if (!key) { setNote('Could not read the key, so the full URL was not copied.'); return; }
    const full = `${s.intakeUrl}?key=${encodeURIComponent(key)}`;
    await copy(full, setNote);
    setNote('Full URL copied — it contains the key, so send it the way you would send a password.');
  });

  const openInstructions = (s: any) => run(s.id, async () => {
    const res = await api.revealLeadSourceKey(s.id);
    setModal({ kind: 'instructions', source: s, text: instructionsText(s.displayName, s.intakeUrl, (res.data as any)?.key ?? '') });
  });

  return (
    <Card title="Lead sources" full>
      {error && (
        <div style={{ fontSize: '12px', color: T.amber, marginBottom: '10px', lineHeight: 1.5 }}>
          Lead sources could not be loaded: {error}
        </div>
      )}

      <div style={{ fontSize: '11px', color: T.dim, marginBottom: '12px', lineHeight: 1.6 }}>
        Each source posts to its own URL with its own key. Every request is stored raw before anything is read from
        it, so a field we do not recognise costs a mapping edit and a reprocess — never a lost lead.
        {data?.countsTruncated && (
          <span style={{ color: T.amber }}> Counts below cover the most recent {data.countsCappedAt} submissions.</span>
        )}
      </div>

      {note && (
        <div style={{ fontSize: '12px', color: T.text, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: '7px', padding: '8px 11px', marginBottom: '10px', lineHeight: 1.5 }}>
          {note}
        </div>
      )}

      {sources.length === 0 && !error && (
        <div style={{ fontSize: '12px', color: T.dim }}>No sources yet — migration 041 seeds them.</div>
      )}

      {sources.map((s) => {
        const open = openId === s.id;
        const total = Object.values(s.counts ?? {}).reduce((n: number, v: any) => n + Number(v), 0);
        return (
          <div key={s.id} style={{ background: T.panel2, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '11px', marginBottom: '8px' }}>
            {/* ---- header line ---- */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: s.active ? T.text : T.dim }}>{s.displayName}</span>
              <button
                onClick={() => toggleActive(s)}
                disabled={busy === s.id}
                title={s.active ? 'Stop accepting posts from this source' : 'Start accepting posts'}
                style={{ ...ghost, color: s.active ? T.green : T.dim, borderColor: s.active ? `${T.greenSoftLine}` : T.border }}
              >
                {s.active ? 'Active' : 'Off'}
              </button>
              <span style={{ fontSize: '11px', color: T.dim }}>
                {s.lastReceivedAt ? `last received ${formatDateTimeInTz(s.lastReceivedAt, tz)}` : 'nothing received yet'}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button style={ghost} disabled={busy === s.id} onClick={() => openInstructions(s)}>Provider instructions</button>
                <button style={primary} disabled={busy === s.id || !s.active} onClick={() => sendTest(s)}>
                  {busy === s.id ? 'Working…' : 'Send test lead'}
                </button>
                <button style={ghost} onClick={() => setOpenId(open ? null : s.id)}>
                  {open ? 'Hide' : 'Details'}
                </button>
              </span>
            </div>

            {/* ---- counts ---- */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '7px' }}>
              {total === 0 && <span style={{ fontSize: '11px', color: T.dim }}>No submissions yet.</span>}
              {Object.entries(s.counts ?? {}).map(([st, n]) => {
                const meta = statusOf(st);
                return (
                  <span key={st} title={meta.hint} style={{ fontSize: '11px', color: meta.color }}>
                    {meta.label} <span style={{ fontFamily: T.mono }}>{String(n)}</span>
                  </span>
                );
              })}
            </div>

            {open && (
              <SourceDetail
                source={s}
                tz={tz}
                onChanged={refetch}
                onReveal={() => reveal(s)}
                onRotate={() => rotate(s)}
                onCopyFullUrl={isAdmin ? () => copyFullUrl(s) : undefined}
                onOpenLog={(logId) => setModal({ kind: 'log', source: s, logId })}
                onNote={setNote}
              />
            )}
          </div>
        );
      })}

      {modal?.kind === 'instructions' && (
        <Modal title={`Provider instructions — ${modal.source.displayName}`} onClose={() => setModal(null)}>
          <div style={{ fontSize: '11px', color: T.amber, marginBottom: '8px', lineHeight: 1.5 }}>
            This text contains the live key. Send it to {modal.source.displayName} directly — not to a shared inbox or a ticket queue.
          </div>
          <textarea readOnly value={modal.text} style={{ ...input, minHeight: '340px', fontFamily: T.mono, fontSize: '11px', lineHeight: 1.5 }} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button style={primary} onClick={() => copy(modal.text ?? '', setNote)}>Copy all</button>
            <button style={ghost} onClick={() => setModal(null)}>Close</button>
          </div>
        </Modal>
      )}

      {modal?.kind === 'log' && modal.logId && (
        <LogRowModal
          id={modal.logId}
          tz={tz}
          onClose={() => setModal(null)}
          onChanged={() => { refetch(); setModal(null); }}
          onNote={setNote}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// one source, expanded
// ---------------------------------------------------------------------------

function SourceDetail({
  source, tz, onChanged, onReveal, onRotate, onCopyFullUrl, onOpenLog, onNote,
}: {
  source: any; tz: string; onChanged: () => void;
  onReveal: () => void; onRotate: () => void;
  /** Admin only — the URL it copies carries the secret. Undefined hides the button. */
  onCopyFullUrl?: () => void;
  onOpenLog: (logId: string) => void; onNote: (s: string) => void;
}) {
  const api = useApiClient();
  const { data: log, refetch: refetchLog } = useApi<any[]>(
    async () => (await api.getLeadSourceLog(source.id, 25)).data || [],
    { dependencies: [source.id] }
  );
  const [map, setMap] = useState<Record<string, string>>(source.fieldMap ?? {});
  const [newKey, setNewKey] = useState('');
  const [newField, setNewField] = useState('full_name');
  const [saving, setSaving] = useState(false);

  const known = KNOWN_MAPPINGS[source.slug];
  const dirty = JSON.stringify(map) !== JSON.stringify(source.fieldMap ?? {});

  const saveMap = async () => {
    setSaving(true);
    try { await api.updateLeadSource(source.id, { fieldMap: map }); onNote('Mapping saved.'); onChanged(); }
    catch (e: any) { onNote(e?.message || 'Could not save the mapping.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, marginTop: '10px', paddingTop: '10px' }}>
      {source.slug === 'meta' && <MetaStatusPanel tz={tz} onNote={onNote} />}
      {/* ---- URL + key ---- */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
        <input readOnly value={source.intakeUrl} style={{ ...input, flex: 1, minWidth: '260px', fontFamily: T.mono }} />
        <button style={ghost} onClick={() => copy(source.intakeUrl, onNote)}>Copy URL</button>
        {onCopyFullUrl && (
          <button
            style={{ ...ghost, color: T.accent, borderColor: `${T.accentSoftLine}` }}
            onClick={onCopyFullUrl}
            title="The endpoint with ?key= attached — what the provider or your developer actually needs. Treat it like a password."
          >
            Copy full URL (with key)
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
        <span style={{ fontSize: '11px', color: T.muted, fontFamily: T.mono }}>key {source.keyPreview}</span>
        <button style={ghost} onClick={onReveal}>Reveal</button>
        <button style={{ ...ghost, color: T.amber, borderColor: `${T.amberSoftLine}` }} onClick={onRotate}>Rotate</button>
        <span style={{ fontSize: '10.5px', color: T.dim }}>
          Rotating stops the old key working at once — only do it if the key has leaked.
        </span>
      </div>

      {/* ---- mapping ---- */}
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, marginBottom: '6px' }}>
        Field mapping
      </div>
      <div style={{ fontSize: '10.5px', color: T.dim, marginBottom: '8px', lineHeight: 1.5 }}>
        Only needed when this source sends field names we do not recognise. Their key on the left, our field on the
        right. Common names — FirstName, email, phone, movedate and so on — are handled without any of this.
      </div>

      {Object.entries(map).length === 0 && (
        <div style={{ fontSize: '11px', color: T.dim, marginBottom: '6px' }}>Nothing mapped — the built-in names are doing the work.</div>
      )}
      {Object.entries(map).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '5px' }}>
          <input readOnly value={k} style={{ ...input, flex: 1, fontFamily: T.mono }} />
          <span style={{ color: T.dim, fontSize: '12px' }}>→</span>
          <select
            value={v}
            onChange={(e) => setMap((m) => ({ ...m, [k]: e.target.value }))}
            style={{ ...input, flex: 1 }}
          >
            {TARGET_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <button
            style={{ ...ghost, color: T.red, borderColor: `${T.redSoftLine}` }}
            onClick={() => setMap((m) => { const n = { ...m }; delete n[k]; return n; })}
          >
            Remove
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '7px', flexWrap: 'wrap' }}>
        <input
          value={newKey} onChange={(e) => setNewKey(e.target.value)}
          placeholder="their field name" style={{ ...input, flex: 1, minWidth: '160px', fontFamily: T.mono }}
        />
        <span style={{ color: T.dim, fontSize: '12px' }}>→</span>
        <select value={newField} onChange={(e) => setNewField(e.target.value)} style={{ ...input, flex: 1, minWidth: '160px' }}>
          {TARGET_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <button
          style={ghost}
          disabled={!newKey.trim()}
          onClick={() => { setMap((m) => ({ ...m, [newKey.trim()]: newField })); setNewKey(''); }}
        >
          Add
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '9px', flexWrap: 'wrap' }}>
        <button style={primary} disabled={!dirty || saving} onClick={saveMap}>
          {saving ? 'Saving…' : 'Save mapping'}
        </button>
        {dirty && <button style={ghost} onClick={() => setMap(source.fieldMap ?? {})}>Discard</button>}
        {known && (
          <button style={ghost} onClick={() => setMap(known)} title="The field names this form posts, read from the live page">
            Use the known {source.slug} mapping
          </button>
        )}
        {known && (
          <span style={{ fontSize: '10.5px', color: T.dim, lineHeight: 1.5 }}>
            Kadence names its fields with hashes, so nothing could guess these.
          </span>
        )}
      </div>

      {/* ---- recent log ---- */}
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, margin: '14px 0 6px' }}>
        Recent submissions
      </div>
      {(log ?? []).length === 0 && <div style={{ fontSize: '11px', color: T.dim }}>Nothing yet.</div>}
      {(log ?? []).map((r) => {
        const meta = statusOf(r.status);
        return (
          <div
            key={r.id}
            onClick={() => onOpenLog(r.id)}
            title={meta.hint}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 8px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', flexWrap: 'wrap' }}
          >
            <span style={{ fontSize: '10px', fontWeight: 700, color: meta.color, minWidth: '64px' }}>{meta.label}</span>
            <span style={{ fontSize: '11px', color: T.dim, fontFamily: T.mono }}>{formatDateTimeInTz(r.receivedAt, tz)}</span>
            {r.jobNo && <span style={{ fontSize: '11px', color: T.accent }}>#{r.jobNo} {r.leadName}</span>}
            {r.error && (
              <span style={{ fontSize: '10.5px', color: T.dim, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.error}
              </span>
            )}
          </div>
        );
      })}
      {(log ?? []).length > 0 && (
        <button style={{ ...ghost, marginTop: '7px' }} onClick={() => refetchLog()}>Refresh</button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meta Lead Ads set-up (072): Meta does not post to the intake URL — its
// webhook (/webhooks/meta) sends a lead id and the CRM fetches the lead. The
// secrets are Railway env vars; this shows only whether each is set, and checks
// the Page token live.
// ---------------------------------------------------------------------------

function MetaStatusPanel({ tz, onNote }: { tz: string; onNote: (s: string) => void }) {
  const api = useApiClient();
  const { data: st, error, loading, refetch } = useApi<any>(async () => (await api.getMetaStatus()).data);
  const line = (ok: boolean | null, label: string, detail?: React.ReactNode) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '12px', padding: '2px 0', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 700, minWidth: '14px', color: ok == null ? T.dim : ok ? T.green : T.red }}>{ok == null ? '–' : ok ? '✓' : '✗'}</span>
      <span style={{ color: T.text, minWidth: '150px' }}>{label}</span>
      {detail && <span style={{ color: T.dim, fontSize: '11px', minWidth: 0, overflowWrap: 'anywhere' }}>{detail}</span>}
    </div>
  );
  const pt = st?.pageToken;
  const last = st?.lastLead;
  return (
    <div style={{ marginBottom: '14px', paddingBottom: '12px', borderBottom: `1px solid ${T.border}` }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, marginBottom: '6px' }}>
        Meta set-up
      </div>
      {error && <div style={{ fontSize: '11px', color: T.amber, marginBottom: '6px' }}>Could not check: {String(error)}</div>}
      {loading && !st && <div style={{ fontSize: '11px', color: T.dim }}>Checking…</div>}
      {st && (
        <>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
            <input readOnly value={st.webhookUrl} style={{ ...input, flex: 1, minWidth: '220px', fontFamily: T.mono }} />
            <button style={ghost} onClick={() => copy(st.webhookUrl, onNote)}>Copy webhook URL</button>
          </div>
          <div style={{ fontSize: '10.5px', color: T.dim, marginBottom: '8px', lineHeight: 1.5 }}>
            Give the developer this callback URL and the META_VERIFY_TOKEN value; in the Meta app they subscribe the Page to the
            "leadgen" field. Graph API {st.graphVersion}. Leads come in through the webhook, not the intake URL below.
          </div>
          {line(st.verifyTokenSet, 'Verify token', st.verifyTokenSet ? 'META_VERIFY_TOKEN is set' : 'set META_VERIFY_TOKEN in Railway')}
          {line(st.appSecretSet, 'App secret', st.appSecretSet ? 'META_APP_SECRET is set — unsigned posts are refused' : 'set META_APP_SECRET in Railway — until then posts are accepted unsigned')}
          {line(pt?.set ? pt.valid : false, 'Page token',
            !pt?.set ? 'set META_PAGE_TOKEN in Railway'
            : pt.valid ? `valid — ${pt.pageName ?? 'Page'} (${pt.pageId})${pt.matchesPageId === false ? ' — NOT the Page in META_PAGE_ID' : ''}`
            : `not valid: ${pt.error}`)}
          {line(st.pageIdSet ? (pt?.matchesPageId ?? true) : null, 'Page id', st.pageIdSet ? 'META_PAGE_ID is set' : 'META_PAGE_ID not set — leads from any Page are accepted')}
          {line(st.leadgenSubscribed, 'Page subscribed to leadgen',
            st.leadgenSubscribed == null ? 'could not check (needs a valid Page token)' : st.leadgenSubscribed ? 'yes' : 'no app is subscribed to "leadgen" on this Page')}
          {line(last ? last.status === 'stored' && !last.error : null, 'Last lead received',
            last ? <>
              {formatDateTimeInTz(last.at, tz)} via {last.via}
              {last.jobNo ? ` — Job ${last.jobNo}` : ''}{last.error ? ` — ${last.error}` : ''}
            </> : 'none yet')}
          {st.lastError && (!last || st.lastError.error !== last.error) &&
            line(false, 'Last problem', `${formatDateTimeInTz(st.lastError.at, tz)} — ${st.lastError.error}`)}
          <div style={{ fontSize: '11px', color: T.dim, margin: '4px 0 0 22px' }}>
            {st.counts.last30Days} Meta lead{st.counts.last30Days === 1 ? '' : 's'} in the last 30 days.{' '}
            <button style={{ ...ghost, padding: '2px 8px' }} onClick={() => refetch()}>Check again</button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// one submission, with its raw body
// ---------------------------------------------------------------------------

function LogRowModal({
  id, tz, onClose, onChanged, onNote,
}: { id: string; tz: string; onClose: () => void; onChanged: () => void; onNote: (s: string) => void }) {
  const api = useApiClient();
  const { data: row, error } = useApi<any>(async () => (await api.getIntakeLogRow(id)).data, { dependencies: [id] });
  const [busy, setBusy] = useState(false);

  const reprocess = async () => {
    setBusy(true);
    try {
      const res = await api.reprocessIntakeLog(id);
      const d: any = res.data;
      onNote(
        d?.status === 'created' ? `Reprocessed — Job #${d.jobNo} created.`
        : d?.status === 'duplicate_flagged' ? `Reprocessed — Job #${d.jobNo} created and flagged as a possible duplicate.`
        : 'Reprocessed, and it is still held: the mapping does not yet cover a name and a phone or email.'
      );
      onChanged();
    } catch (e: any) {
      onNote(e?.message || 'Reprocessing failed.');
    } finally {
      setBusy(false);
    }
  };

  const meta = row ? statusOf(row.status) : null;

  return (
    <Modal title="Submission" onClose={onClose}>
      {error && <div style={{ fontSize: '12px', color: T.red }}>{error}</div>}
      {!row && !error && <div style={{ fontSize: '12px', color: T.dim }}>Loading…</div>}
      {row && (
        <>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: meta!.color }}>{meta!.label}</span>
            <span style={{ fontSize: '11px', color: T.dim, fontFamily: T.mono }}>{formatDateTimeInTz(row.receivedAt, tz)}</span>
            <span style={{ fontSize: '11px', color: T.dim }}>{row.sourceName}</span>
            {row.contentType && <span style={{ fontSize: '11px', color: T.dim, fontFamily: T.mono }}>{row.contentType}</span>}
            {row.ip && <span style={{ fontSize: '11px', color: T.dim, fontFamily: T.mono }}>{row.ip}</span>}
          </div>
          <div style={{ fontSize: '11px', color: T.dim, marginBottom: '10px', lineHeight: 1.5 }}>{meta!.hint}</div>

          {row.jobNo && (
            <div style={{ fontSize: '12px', color: T.accent, marginBottom: '10px' }}>
              Became Job #{row.jobNo} — {row.leadName}
            </div>
          )}
          {row.error && (
            <div style={{ fontSize: '11.5px', color: T.amber, marginBottom: '10px', lineHeight: 1.5 }}>{row.error}</div>
          )}

          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, marginBottom: '5px' }}>
            Raw payload — exactly what they sent
          </div>
          <textarea readOnly value={row.rawBody ?? ''} style={{ ...input, minHeight: '180px', fontFamily: T.mono, fontSize: '11px' }} />

          {row.parsed && (
            <>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', color: T.dim, fontWeight: 700, margin: '10px 0 5px' }}>
                What we made of it
              </div>
              <textarea readOnly value={JSON.stringify(row.parsed, null, 2)} style={{ ...input, minHeight: '140px', fontFamily: T.mono, fontSize: '11px' }} />
            </>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {!row.leadId && (
              <button style={primary} disabled={busy} onClick={reprocess}>
                {busy ? 'Reprocessing…' : 'Reprocess'}
              </button>
            )}
            <button style={ghost} onClick={() => copy(row.rawBody ?? '', onNote)}>Copy payload</button>
            <button style={ghost} onClick={onClose}>Close</button>
            {row.leadId && (
              <span style={{ fontSize: '10.5px', color: T.dim, lineHeight: 1.5 }}>
                This one already made a lead, so it cannot be reprocessed — that would make a second.
              </span>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// chrome
// ---------------------------------------------------------------------------

function Card({ title, full, children }: { title: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: '10px', padding: '14px', gridColumn: full ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: T.text, marginBottom: '12px' }}>
        <Icons.inbox size={16} aria-hidden="true" />{title}
      </div>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: '12px', padding: '18px', width: '760px', maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: T.text }}>{title}</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: T.muted, cursor: 'pointer' }} aria-label="Close">
            <Icons.close size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
